import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BarChart3, Search, ArrowUpDown, Filter, CreditCard, Plus, Pencil, Clock } from 'lucide-react';
import { PieChart as RePieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
import InlineEdit from '@/components/InlineEdit';

const COLORS = ['#3A8C8C', '#C67D5B', '#7DA68A', '#C4727F', '#D4D0C8', '#2C2C2E'];
const statuses = ['Action Required', 'Follow-Up', 'In Progress', 'Needs Review', 'In Execution', 'Completed'];
const taskTypes = ['General', 'Follow-up', 'Drawing', 'Order', 'Delivery', 'Payment'];
const priorities = ['High', 'Medium', 'Low'];
const emptyTaskForm = { title: '', project_id: '', project_name: '', assigned_to: '', assigned_name: '', priority: 'Medium', status: 'Action Required', due_date: '', description: '' };

function formatDate(d) {
  if (!d) return '-';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
}

function formatINR(n) {
  if (!n && n !== 0) return '0';
  return Number(n).toLocaleString('en-IN');
}

function getPriorityDot(task) {
  if (!task.due_date || task.status === 'Completed') return null;
  const days = Math.ceil((new Date(task.due_date) - Date.now()) / 86400000);
  if (days < 0) return { color: 'bg-[#C4727F]', label: 'Overdue' };
  if (days <= 2) return { color: 'bg-[#C67D5B]', label: 'Urgent' };
  return { color: 'bg-[#7DA68A]', label: 'On Track' };
}

export default function ReportsPage() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [team, setTeam] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [leadStats, setLeadStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterProject, setFilterProject] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAssigned, setFilterAssigned] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortCol, setSortCol] = useState('due_date');
  const [sortDir, setSortDir] = useState('asc');

  // Add/Edit task
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyTaskForm);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [t, p, tm, op, ls] = await Promise.all([
        api.get('/tasks'),
        api.get('/projects'),
        api.get('/team'),
        api.get('/reports/outstanding-payments'),
        api.get('/leads/stats/loss-reasons'),
      ]);
      setTasks(t.data);
      setProjects(p.data);
      setTeam(tm.data);
      setOutstanding(op.data || []);
      setLeadStats(ls.data || null);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreateTask = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    try {
      await api.post('/tasks', form);
      toast.success('Task created');
      setShowCreate(false);
      setForm(emptyTaskForm);
      loadData();
    } catch { toast.error('Failed to create task'); }
  };

  const updateTaskField = async (taskId, field, value) => {
    try {
      await api.put(`/tasks/${taskId}`, { [field]: value });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, [field]: value } : t));
    } catch { toast.error('Update failed'); }
  };

  const openTaskEditor = (task) => { setSelectedTask(task); setShowTaskDetail(true); };

  // Compute priority status (on_track/urgent/overdue) dynamically
  const enrichedTasks = tasks.map(t => ({
    ...t,
    priorityDot: getPriorityDot(t)
  }));

  // Apply filters
  const filtered = enrichedTasks.filter(t => {
    if (filterProject !== 'all' && t.project_id !== filterProject) return false;
    if (filterPriority !== 'all') {
      if (filterPriority === 'On Track' && t.priorityDot?.label !== 'On Track') return false;
      if (filterPriority === 'Urgent' && t.priorityDot?.label !== 'Urgent') return false;
      if (filterPriority === 'Overdue' && t.priorityDot?.label !== 'Overdue') return false;
    }
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    if (filterAssigned !== 'all' && t.assigned_to !== filterAssigned) return false;
    if (filterType !== 'all' && t.task_type !== filterType) return false;
    return true;
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortCol] || '';
    const bv = b[sortCol] || '';
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  // Follow-ups only
  const followUps = enrichedTasks.filter(t => t.task_type === 'Follow-up' || t.status === 'Follow-Up');

  // Chart data
  const statusCounts = {};
  statuses.forEach(s => { statusCounts[s] = 0; });
  tasks.forEach(t => { if (statusCounts[t.status] !== undefined) statusCounts[t.status]++; });
  const statusData = Object.entries(statusCounts).map(([name, value]) => ({ name, value })).filter(d => d.value > 0);

  const priorityCounts = { 'On Track': 0, 'Urgent': 0, 'Overdue': 0 };
  enrichedTasks.forEach(t => { if (t.priorityDot?.label) priorityCounts[t.priorityDot.label]++; });
  const priorityData = Object.entries(priorityCounts).map(([name, value]) => ({ name, value }));

  const projectCompletion = projects.map(p => {
    const pTasks = tasks.filter(t => t.project_id === p.id);
    const completed = pTasks.filter(t => t.status === 'Completed').length;
    const total = pTasks.length;
    return { name: p.name, completion: total > 0 ? Math.round((completed / total) * 100) : 0, total };
  }).filter(p => p.total > 0);

  // Overdue trend (last 30 days) - simulated from current data
  const overdueByDay = [];
  const now = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().slice(0, 10);
    const overdue = tasks.filter(t => {
      if (t.status === 'Completed') return false;
      if (!t.due_date) return false;
      return t.due_date.slice(0, 10) < dateStr;
    }).length;
    overdueByDay.push({ date: `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`, overdue });
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  const statusBadgeColor = (s) => {
    if (s === 'Completed') return 'bg-[#7DA68A]/10 text-[#7DA68A]';
    if (s === 'Action Required') return 'bg-[#3A8C8C]/10 text-[#3A8C8C]';
    return 'bg-[#E8E6E1] text-[#2C2C2E]';
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="reports-page">
      <div>
        <h1 className="section-heading text-2xl font-['Manrope'] font-bold">Reports</h1>
        <p className="text-sm text-[#6B7280] mt-0.5">Consolidated task overview across all projects</p>
      </div>

      <Tabs defaultValue="all-tasks" className="w-full">
        <TabsList className="bg-[#E8E6E1]/50 rounded-xl p-1">
          <TabsTrigger value="all-tasks" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-all-tasks">All Tasks</TabsTrigger>
          <TabsTrigger value="follow-ups" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-follow-ups">Follow-Ups</TabsTrigger>
          <TabsTrigger value="outstanding" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-outstanding-payments">Outstanding Payments</TabsTrigger>
          <TabsTrigger value="leads" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-lead-pipeline">Lead Pipeline</TabsTrigger>
          <TabsTrigger value="charts" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-charts">Visual Summary</TabsTrigger>
        </TabsList>

        {/* All Tasks Report */}
        <TabsContent value="all-tasks" className="mt-6 space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="report-filters">
            <Filter size={14} strokeWidth={1.5} className="text-[#6B7280]" />
            <Select value={filterProject} onValueChange={setFilterProject}>
              <SelectTrigger className="h-8 w-36 rounded-lg border-[#E8E6E1] text-[11px]" data-testid="report-filter-project"><SelectValue placeholder="All Projects" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="h-8 w-28 rounded-lg border-[#E8E6E1] text-[11px]" data-testid="report-filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="On Track">On Track</SelectItem>
                <SelectItem value="Urgent">Urgent</SelectItem>
                <SelectItem value="Overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-8 w-32 rounded-lg border-[#E8E6E1] text-[11px]" data-testid="report-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterAssigned} onValueChange={setFilterAssigned}>
              <SelectTrigger className="h-8 w-32 rounded-lg border-[#E8E6E1] text-[11px]" data-testid="report-filter-assigned"><SelectValue placeholder="Assigned To" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Members</SelectItem>
                {team.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="h-8 w-28 rounded-lg border-[#E8E6E1] text-[11px]" data-testid="report-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {taskTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Task Table */}
          <div className="glass-card rounded-2xl overflow-hidden" data-testid="all-tasks-table">
            <div className="p-4 border-b border-[#E8E6E1] flex items-center justify-between">
              <h4 className="section-heading text-sm font-semibold font-['Manrope']">All Tasks</h4>
              <div className="flex items-center gap-3">
                <span className="text-xs text-[#6B7280]">{sorted.length} of {tasks.length} tasks</span>
                <button
                  onClick={() => { setForm({ ...emptyTaskForm, project_id: filterProject !== 'all' ? filterProject : '', project_name: filterProject !== 'all' ? (projects.find(p => p.id === filterProject)?.name || '') : '' }); setShowCreate(true); }}
                  data-testid="report-create-task-btn"
                  className="bg-[#3A8C8C] text-white rounded-full pl-3 pr-4 py-1.5 text-xs font-medium hover:bg-[#2E7A7A] transition-colors flex items-center gap-1.5 active:scale-95"
                >
                  <Plus size={13} strokeWidth={1.5} /> New Task
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#FAFAF8]">
                    {[
                      { key: 'title', label: 'Task Title' },
                      { key: 'project_name', label: 'Project' },
                      { key: 'assigned_name', label: 'Assigned To' },
                      { key: 'priority', label: 'Priority' },
                      { key: 'status', label: 'Status' },
                      { key: 'due_date', label: 'Due Date' },
                      { key: 'task_type', label: 'Task Type' },
                    ].map(col => (
                      <TableHead key={col.key} onClick={() => toggleSort(col.key)}
                        className="text-xs cursor-pointer hover:text-[#2C2C2E] select-none">
                        <span className="flex items-center gap-1">{col.label}
                          {sortCol === col.key && <ArrowUpDown size={10} className="text-[#3A8C8C]" />}
                        </span>
                      </TableHead>
                    ))}
                    <TableHead className="text-xs w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.slice(0, 50).map(t => {
                    const dot = t.priorityDot;
                    return (
                      <TableRow key={t.id} className="cursor-pointer hover:bg-[#FAFAF8]/60"
                        onClick={() => navigate(`/projects/${t.project_id}?tab=tasks`)}
                        data-testid={`report-task-row-${t.id}`}>
                        <TableCell className={`text-sm font-medium ${t.status === 'Completed' ? 'line-through text-[#6B7280]' : 'text-[#2C2C2E]'}`}>{t.title}</TableCell>
                        <TableCell><Badge className="bg-[#E8E6E1] text-[#2C2C2E] text-[9px]">{t.project_name}</Badge></TableCell>
                        <TableCell className="text-sm text-[#6B7280]">
                          <div className="flex items-center gap-1.5">
                            {t.assigned_name && (
                              <div className="w-5 h-5 rounded-full bg-[#3A8C8C]/10 flex items-center justify-center">
                                <span className="text-[8px] font-medium text-[#3A8C8C]">{t.assigned_name?.[0]}</span>
                              </div>
                            )}
                            <span className="text-xs">{t.assigned_name || '-'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {dot && <div className={`w-2 h-2 rounded-full ${dot.color}`} />}
                            <span className="text-[10px] text-[#6B7280]">{dot?.label || '-'}</span>
                          </div>
                        </TableCell>
                        <TableCell><Badge className={`text-[9px] ${statusBadgeColor(t.status)}`}>{t.status}</Badge></TableCell>
                        <TableCell className={`text-xs ${dot?.label === 'Overdue' ? 'text-[#C4727F] font-medium' : 'text-[#6B7280]'}`}>{formatDate(t.due_date)}</TableCell>
                        <TableCell><Badge className="bg-[#E8E6E1] text-[#2C2C2E] text-[9px]">{t.task_type || 'General'}</Badge></TableCell>
                        <TableCell>
                          <button
                            onClick={(e) => { e.stopPropagation(); openTaskEditor(t); }}
                            className="p-1.5 rounded-lg text-[#6B7280] hover:text-[#3A8C8C] hover:bg-[#3A8C8C]/10 transition-colors"
                            title="Edit task"
                            data-testid={`report-task-edit-${t.id}`}
                          >
                            <Pencil size={13} strokeWidth={1.5} />
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {sorted.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-xs text-[#6B7280]">No tasks match filters</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* Follow-Ups */}
        <TabsContent value="follow-ups" className="mt-6">
          <div className="glass-card rounded-2xl overflow-hidden" data-testid="follow-ups-table">
            <div className="p-4 border-b border-[#E8E6E1]">
              <h4 className="section-heading text-sm font-semibold font-['Manrope']">Follow-Ups</h4>
              <p className="text-xs text-[#6B7280] mt-0.5">{followUps.length} follow-up tasks</p>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAF8]">
                  <TableHead className="text-xs">Task Title</TableHead>
                  <TableHead className="text-xs">Project</TableHead>
                  <TableHead className="text-xs">Assigned To</TableHead>
                  <TableHead className="text-xs">Priority</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Due Date</TableHead>
                  <TableHead className="text-xs w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {followUps.map(t => {
                  const dot = t.priorityDot;
                  return (
                    <TableRow key={t.id} className="cursor-pointer hover:bg-[#FAFAF8]/60" onClick={() => navigate(`/projects/${t.project_id}?tab=tasks`)}>
                      <TableCell className="text-sm font-medium text-[#2C2C2E]">{t.title}</TableCell>
                      <TableCell><Badge className="bg-[#E8E6E1] text-[#2C2C2E] text-[9px]">{t.project_name}</Badge></TableCell>
                      <TableCell className="text-xs text-[#6B7280]">{t.assigned_name || '-'}</TableCell>
                      <TableCell><div className="flex items-center gap-1.5">{dot && <div className={`w-2 h-2 rounded-full ${dot.color}`} />}<span className="text-[10px]">{dot?.label || '-'}</span></div></TableCell>
                      <TableCell><Badge className={`text-[9px] ${statusBadgeColor(t.status)}`}>{t.status}</Badge></TableCell>
                      <TableCell className="text-xs text-[#6B7280]">{formatDate(t.due_date)}</TableCell>
                      <TableCell>
                        <button
                          onClick={(e) => { e.stopPropagation(); openTaskEditor(t); }}
                          className="p-1.5 rounded-lg text-[#6B7280] hover:text-[#3A8C8C] hover:bg-[#3A8C8C]/10 transition-colors"
                          title="Edit task"
                        >
                          <Pencil size={13} strokeWidth={1.5} />
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {followUps.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-xs text-[#6B7280]">No follow-up tasks</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Outstanding Payments */}
        <TabsContent value="outstanding" className="mt-6 space-y-4" data-testid="outstanding-payments-panel">
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="glass-card rounded-xl p-4">
              <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Total Outstanding</p>
              <p className="text-lg font-bold text-[#2C2C2E] font-['Manrope'] mt-1" data-testid="outstanding-total">
                Rs {formatINR(outstanding.reduce((s, o) => s + (o.pending_amount || 0), 0))}
              </p>
            </div>
            <div className="glass-card rounded-xl p-4">
              <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Overdue</p>
              <p className="text-lg font-bold text-[#C4727F] font-['Manrope'] mt-1">
                Rs {formatINR(outstanding.filter(o => o.time_state === 'Overdue').reduce((s, o) => s + (o.pending_amount || 0), 0))}
              </p>
            </div>
            <div className="glass-card rounded-xl p-4">
              <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Due Soon</p>
              <p className="text-lg font-bold text-[#C67D5B] font-['Manrope'] mt-1">
                Rs {formatINR(outstanding.filter(o => o.time_state === 'Due Soon').reduce((s, o) => s + (o.pending_amount || 0), 0))}
              </p>
            </div>
            <div className="glass-card rounded-xl p-4">
              <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Open Bills</p>
              <p className="text-lg font-bold text-[#3A8C8C] font-['Manrope'] mt-1">{outstanding.length}</p>
            </div>
          </div>

          <div className="glass-card rounded-2xl overflow-hidden" data-testid="outstanding-payments-table">
            <div className="p-4 border-b border-[#E8E6E1] flex items-center justify-between">
              <div>
                <h4 className="section-heading text-sm font-semibold font-['Manrope']">Outstanding Payments</h4>
                <p className="text-xs text-[#6B7280] mt-0.5">All pending bills across projects, sorted by pending amount</p>
              </div>
              <span className="text-xs text-[#6B7280]">{outstanding.length} {outstanding.length === 1 ? 'bill' : 'bills'}</span>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#FAFAF8]">
                    <TableHead className="text-xs">Project</TableHead>
                    <TableHead className="text-xs">Vendor</TableHead>
                    <TableHead className="text-xs">Bill Type</TableHead>
                    <TableHead className="text-xs">Bill Date</TableHead>
                    <TableHead className="text-xs text-right">Bill Amount</TableHead>
                    <TableHead className="text-xs text-right">Paid</TableHead>
                    <TableHead className="text-xs text-right">Pending</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outstanding.map(o => (
                    <TableRow
                      key={`${o.project_id}-${o.entry_id}`}
                      className="cursor-pointer hover:bg-[#FAFAF8]/60"
                      onClick={() => navigate(`/projects/${o.project_id}`)}
                      data-testid={`outstanding-row-${o.entry_id}`}
                    >
                      <TableCell><Badge className="bg-[#E8E6E1] text-[#2C2C2E] text-[9px]">{o.project_name}</Badge></TableCell>
                      <TableCell className="text-sm font-medium text-[#2C2C2E]">{o.vendor_name || '—'}</TableCell>
                      <TableCell className="text-xs text-[#6B7280]">{o.bill_type || '—'}{o.bill_number ? ` · #${o.bill_number}` : ''}</TableCell>
                      <TableCell className="text-xs text-[#6B7280]">{formatDate(o.bill_date)}</TableCell>
                      <TableCell className="text-sm text-right font-medium text-[#2C2C2E]">Rs {formatINR(o.bill_amount)}</TableCell>
                      <TableCell className="text-sm text-right text-[#7DA68A]">Rs {formatINR(o.total_paid)}</TableCell>
                      <TableCell className="text-sm text-right font-semibold text-[#C67D5B]">Rs {formatINR(o.pending_amount)}</TableCell>
                      <TableCell>
                        <Badge className={`text-[9px] ${o.time_state === 'Overdue' ? 'bg-[#C4727F]/10 text-[#C4727F]' : o.time_state === 'Due Soon' ? 'bg-[#C67D5B]/10 text-[#C67D5B]' : 'bg-[#7DA68A]/10 text-[#7DA68A]'}`}>
                          {o.time_state}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {outstanding.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-xs text-[#6B7280]">
                        <CreditCard size={28} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-2" />
                        No outstanding payments. All bills are cleared.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* Lead Pipeline & Loss Analytics */}
        <TabsContent value="leads" className="mt-6 space-y-4" data-testid="lead-pipeline-panel">
          {!leadStats ? (
            <div className="glass-card rounded-2xl p-12 text-center text-sm text-[#6B7280]">No lead data yet.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="glass-card rounded-xl p-4">
                  <p className="text-[10px] text-[#6B7280] uppercase tracking-wider">Total Leads</p>
                  <p className="text-2xl font-['Manrope'] font-bold text-[#2C2C2E] mt-1" data-testid="lead-stat-total">{leadStats.total_leads}</p>
                </div>
                <div className="glass-card rounded-xl p-4">
                  <p className="text-[10px] text-[#6B7280] uppercase tracking-wider">Won</p>
                  <p className="text-2xl font-['Manrope'] font-bold text-[#7DA68A] mt-1" data-testid="lead-stat-won">{leadStats.won}</p>
                </div>
                <div className="glass-card rounded-xl p-4">
                  <p className="text-[10px] text-[#6B7280] uppercase tracking-wider">Lost</p>
                  <p className="text-2xl font-['Manrope'] font-bold text-[#C4727F] mt-1" data-testid="lead-stat-lost">{leadStats.lost}</p>
                </div>
                <div className="glass-card rounded-xl p-4">
                  <p className="text-[10px] text-[#6B7280] uppercase tracking-wider">Active</p>
                  <p className="text-2xl font-['Manrope'] font-bold text-[#3A8C8C] mt-1">{leadStats.active}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="glass-card rounded-xl p-4">
                  <p className="text-[10px] text-[#6B7280] uppercase tracking-wider">Conversion Rate</p>
                  <p className="text-xl font-semibold text-[#2C2C2E] mt-1">{leadStats.conversion_rate}%</p>
                  <p className="text-[10px] text-[#6B7280] mt-0.5">won / total</p>
                </div>
                <div className="glass-card rounded-xl p-4">
                  <p className="text-[10px] text-[#6B7280] uppercase tracking-wider">Win Rate (Decided)</p>
                  <p className="text-xl font-semibold text-[#2C2C2E] mt-1">{leadStats.win_rate_decided}%</p>
                  <p className="text-[10px] text-[#6B7280] mt-0.5">won / (won + lost)</p>
                </div>
                <div className="glass-card rounded-xl p-4">
                  <p className="text-[10px] text-[#6B7280] uppercase tracking-wider">Won vs Lost Ratio</p>
                  <p className="text-xl font-semibold text-[#2C2C2E] mt-1">{leadStats.won_vs_lost_ratio}</p>
                  <p className="text-[10px] text-[#6B7280] mt-0.5">{leadStats.won} won : {leadStats.lost} lost</p>
                </div>
              </div>

              {/* Loss reasons breakdown */}
              <div className="glass-card rounded-2xl p-5" data-testid="loss-reasons-breakdown">
                <h4 className="section-heading text-sm font-semibold font-['Manrope'] mb-4">Loss Reasons Breakdown</h4>
                {leadStats.loss_reasons.length === 0 ? (
                  <p className="text-xs text-[#6B7280] text-center py-6">No lost leads yet.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ResponsiveContainer width="100%" height={250}>
                      <RePieChart>
                        <Pie data={leadStats.loss_reasons.map(r => ({ name: r.reason, value: r.count }))} dataKey="value" nameKey="name" outerRadius={90} label={false}>
                          {leadStats.loss_reasons.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                      </RePieChart>
                    </ResponsiveContainer>
                    <div className="space-y-2">
                      {leadStats.loss_reasons.map((r, i) => (
                        <div key={r.reason} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                            <span className="text-[#2C2C2E]">{r.reason}</span>
                          </div>
                          <span className="font-semibold text-[#2C2C2E]" data-testid={`loss-reason-count-${r.reason}`}>{r.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </TabsContent>


        {/* Visual Summary */}
        <TabsContent value="charts" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tasks by Status (Donut) */}
            <div className="glass-card rounded-2xl p-5" data-testid="chart-status">
              <h4 className="section-heading text-sm font-semibold font-['Manrope'] mb-4">Tasks by Status</h4>
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <RePieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3} stroke="none">
                      {statusData.map((entry, i) => <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E8E6E1', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', fontSize: '12px' }} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                  </RePieChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-[#6B7280] text-center py-8">No data</p>}
            </div>

            {/* Tasks by Priority (Bar) */}
            <div className="glass-card rounded-2xl p-5" data-testid="chart-priority">
              <h4 className="section-heading text-sm font-semibold font-['Manrope'] mb-4">Tasks by Priority</h4>
              {priorityData.some(d => d.value > 0) ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={priorityData} barSize={40}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E8E6E1', fontSize: '12px' }} />
                    <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                      {priorityData.map((e) => <Cell key={e.name} fill={e.name === 'Overdue' ? '#C4727F' : e.name === 'Urgent' ? '#C67D5B' : '#7DA68A'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-[#6B7280] text-center py-8">No data</p>}
            </div>

            {/* Project Completion (Horizontal Bar) */}
            <div className="glass-card rounded-2xl p-5" data-testid="chart-completion">
              <h4 className="section-heading text-sm font-semibold font-['Manrope'] mb-4">Project-wise Completion</h4>
              {projectCompletion.length > 0 ? (
                <div className="space-y-3">
                  {projectCompletion.map((p, i) => (
                    <div key={p.name} className="flex items-center gap-3">
                      <span className="text-xs text-[#2C2C2E] w-32 truncate">{p.name}</span>
                      <div className="flex-1 h-2 bg-[#E8E6E1] rounded-full overflow-hidden">
                        <div className="h-full bg-[#3A8C8C] rounded-full transition-all" style={{ width: `${p.completion}%` }} />
                      </div>
                      <span className="text-xs text-[#6B7280] w-10 text-right">{p.completion}%</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-[#6B7280] text-center py-8">No projects with tasks</p>}
            </div>

            {/* Overdue Tasks Trend (Line) */}
            <div className="glass-card rounded-2xl p-5" data-testid="chart-overdue-trend">
              <h4 className="section-heading text-sm font-semibold font-['Manrope'] mb-4">Overdue Tasks Trend (30 days)</h4>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={overdueByDay.filter((_, i) => i % 3 === 0 || i === overdueByDay.length - 1)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E8E6E1', fontSize: '12px' }} />
                  <Line type="monotone" dataKey="overdue" stroke="#C4727F" strokeWidth={2} dot={{ r: 3, fill: '#C4727F' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Task Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-['Manrope']">New Task</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs text-[#6B7280] uppercase">Title</Label><Input data-testid="report-task-title-input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#6B7280] uppercase">Project</Label>
                <Select value={form.project_id} onValueChange={v => { const p = projects.find(x => x.id === v); setForm({...form, project_id: v, project_name: p?.name || ''}); }}>
                  <SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Assign To</Label>
                <Select value={form.assigned_to} onValueChange={v => { const m = team.find(x => x.id === v); setForm({...form, assigned_to: v, assigned_name: m?.name || ''}); }}>
                  <SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]"><SelectValue placeholder="Select member" /></SelectTrigger>
                  <SelectContent>{team.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#6B7280] uppercase">Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}><SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]"><SelectValue /></SelectTrigger><SelectContent>{priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
            </div>
            <div><Label className="text-xs text-[#6B7280] uppercase">Task Type</Label>
              <Select value={form.task_type || 'General'} onValueChange={v => setForm({...form, task_type: v})}><SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]"><SelectValue /></SelectTrigger><SelectContent>{taskTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-xs text-[#6B7280] uppercase">Description</Label><textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1 w-full h-20 px-4 py-2 bg-white border border-[#D4D0C8] rounded-xl text-sm resize-none" /></div>
            <button onClick={handleCreateTask} data-testid="report-submit-task-btn" className="w-full h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors">Create Task</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={showTaskDetail} onOpenChange={setShowTaskDetail}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-['Manrope']">Task Details</DialogTitle></DialogHeader>
          {selectedTask && (
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Title</Label>
                <InlineEdit
                  value={selectedTask.title}
                  onSave={v => { updateTaskField(selectedTask.id, 'title', v); setSelectedTask(prev => ({...prev, title: v})); }}
                  className="text-base font-medium text-[#2C2C2E] mt-1"
                  testId="report-task-detail-title"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[#6B7280] uppercase">Status</Label>
                  <Select value={selectedTask.status} onValueChange={v => { updateTaskField(selectedTask.id, 'status', v); setSelectedTask(prev => ({...prev, status: v})); }}>
                    <SelectTrigger className="mt-1 h-9 rounded-lg border-[#D4D0C8]"><SelectValue /></SelectTrigger>
                    <SelectContent>{statuses.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#6B7280] uppercase">Priority</Label>
                  <Select value={selectedTask.priority} onValueChange={v => { updateTaskField(selectedTask.id, 'priority', v); setSelectedTask(prev => ({...prev, priority: v})); }}>
                    <SelectTrigger className="mt-1 h-9 rounded-lg border-[#D4D0C8]"><SelectValue /></SelectTrigger>
                    <SelectContent>{priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[#6B7280] uppercase">Due Date</Label>
                  <Input
                    type="date"
                    value={selectedTask.due_date?.slice(0,10) || ''}
                    onChange={e => { updateTaskField(selectedTask.id, 'due_date', e.target.value); setSelectedTask(prev => ({...prev, due_date: e.target.value})); }}
                    className="mt-1 h-9 rounded-lg border-[#D4D0C8]"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#6B7280] uppercase">Assigned To</Label>
                  <Select
                    value={selectedTask.assigned_to || ''}
                    onValueChange={v => {
                      const m = team.find(x => x.id === v);
                      updateTaskField(selectedTask.id, 'assigned_to', v);
                      updateTaskField(selectedTask.id, 'assigned_name', m?.name || '');
                      setSelectedTask(prev => ({...prev, assigned_to: v, assigned_name: m?.name || ''}));
                    }}
                  >
                    <SelectTrigger className="mt-1 h-9 rounded-lg border-[#D4D0C8]"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>{team.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Task Type</Label>
                <Select value={selectedTask.task_type || 'General'} onValueChange={v => { updateTaskField(selectedTask.id, 'task_type', v); setSelectedTask(prev => ({...prev, task_type: v})); }}>
                  <SelectTrigger className="mt-1 h-9 rounded-lg border-[#D4D0C8]"><SelectValue /></SelectTrigger>
                  <SelectContent>{taskTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Description</Label>
                <textarea
                  value={selectedTask.description || ''}
                  onChange={e => { updateTaskField(selectedTask.id, 'description', e.target.value); setSelectedTask(prev => ({...prev, description: e.target.value})); }}
                  className="mt-1 w-full h-24 px-3 py-2 text-sm border border-[#D4D0C8] rounded-lg resize-none"
                  placeholder="Add task description..."
                />
              </div>

              {selectedTask.activity_log && selectedTask.activity_log.length > 0 && (
                <div>
                  <Label className="text-xs text-[#6B7280] uppercase mb-2 block">Activity Log</Label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {selectedTask.activity_log.slice().reverse().map((activity, idx) => (
                      <div key={activity.id || idx} className="flex items-start gap-2 text-xs">
                        <Clock size={12} className="text-[#6B7280] mt-0.5 shrink-0" />
                        <div>
                          <span className="text-[#2C2C2E]">{activity.user_name || 'System'}</span>
                          <span className="text-[#6B7280]"> changed status from </span>
                          <Badge className="bg-[#E8E6E1] text-[#2C2C2E] text-[9px] mx-0.5">{activity.from_status}</Badge>
                          <span className="text-[#6B7280]"> to </span>
                          <Badge className="bg-[#3A8C8C]/10 text-[#3A8C8C] text-[9px] mx-0.5">{activity.to_status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-[#E8E6E1]">
                <button
                  onClick={() => { setShowTaskDetail(false); navigate(`/projects/${selectedTask.project_id}?tab=tasks`); }}
                  className="text-[10px] text-[#3A8C8C] hover:underline"
                  data-testid="report-task-goto-project"
                >
                  Open in project &rarr;
                </button>
                <button
                  onClick={() => setShowTaskDetail(false)}
                  className="px-4 py-2 bg-[#3A8C8C] text-white rounded-lg text-xs font-medium hover:bg-[#2E7A7A] transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
